import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Submit Bank Memo Remittance Step
 *
 * Submits one cash receipt's bank memo as a `bank_memo` remittance through
 * remittance intake: the memo becomes a text document on the receipt, the AR
 * Remittance Document Ingestion workflow extracts it like any remittance
 * document, and the advice links straight to the receipt.
 *
 * Idempotent per receipt, so a resent bank email does not submit twice. A
 * receipt another remittance already explains is skipped, as is one whose
 * org has no active remittance ingestion workflow; `skipReason` says which.
 */
export const submitBankMemoRemittanceStep = defineStepConfig({
  type: "submit_bank_memo_remittance",
  name: "Submit Bank Memo Remittance",
  description:
    "Submit a cash receipt's bank memo as a remittance linked to that receipt",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    bankTransactionId: z
      .string()
      .describe("The cash receipt whose memo to submit"),
    invoiceNumbers: z
      .array(z.string())
      .optional()
      .describe(
        "Invoice numbers the memo names (from Classify Bank Memo), listed in the memo document so extraction makes one line each",
      ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["submitted", "skipped"])
      .describe("Whether a remittance run was started"),
    skipReason: z
      .string()
      .nullable()
      .describe(
        "Why it was skipped: receipt_not_found, no_memo, already_submitted, already_explained or workflow_not_runnable",
      ),
    remittanceAdviceId: z
      .string()
      .nullable()
      .describe(
        "The receipt's existing bank_memo remittance, when skipped as already submitted",
      ),
    workflowRunId: z
      .string()
      .nullable()
      .describe("The remittance ingestion run started, when submitted"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 3,
      initialInterval: "5 seconds",
      backoffCoefficient: 2,
    },
  },
});
